import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import AppTopbar from '../layout/AppTopbar';
import AppSidebar from '../layout/AppSidebar';
import AppSearch from '../layout/AppSearch';
import AppConfig from '../layout/AppConfig';
import AppFooter from '../layout/AppFooter';
// import QuickCreateLeadFAB from './QuickCreateLeadFAB';  // Temporarily disabled
import { LayoutProvider, LayoutContext } from '../layout/context/layoutcontext';
import { MaskingProvider } from '../layout/context/maskingcontext';
import { MessagingProvider } from '../contexts/MessagingContext';
import { NotificationProvider } from '../contexts/NotificationContext';
import { classNames, DomHandler } from 'primereact/utils';
import { useEventListener, useResizeListener } from 'primereact/hooks';
import { Button } from 'primereact/button';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import '../styles/layout/layout.scss';
import ManualSwitchBackButton from './ManualSwitchBackButton';
import MobileBottomNav from './MobileBottomNav';

const DashboardLayoutWrapper = () => {
    const sidebarRef = useRef(null);
    const topbarRef = useRef(null);
    const [user, setUser] = useState(null);
    const [iframeHeight, setIframeHeight] = useState('700px');
    const navigate = useNavigate();
    const location = useLocation();
    const { layoutConfig, layoutState, setLayoutState, isSlim, isCompact, isHorizontal, isDesktop, isTablet, isMobile, isTouchDevice, selectedLoanUrl, setSelectedLoanUrl } = useContext(LayoutContext);
    
    // Debug logging for loan URL
    useEffect(() => {
        console.log('DashboardLayout - selectedLoanUrl changed:', selectedLoanUrl);
    }, [selectedLoanUrl]);

    // Handle browser back button when iframe is open
    useEffect(() => {
        if (selectedLoanUrl) {
            // Push a history entry when iframe opens
            window.history.pushState({ iframeOpen: true }, '', window.location.href);

            // Handle browser back button
            const handlePopState = (event) => {
                if (selectedLoanUrl) {
                    console.log('🔙 Browser back button pressed, closing iframe');
                    setSelectedLoanUrl('');
                    // Prevent default navigation by pushing state back
                    event.preventDefault();
                }
            };

            window.addEventListener('popstate', handlePopState);

            return () => {
                window.removeEventListener('popstate', handlePopState);
            };
        }
    }, [selectedLoanUrl, setSelectedLoanUrl]);

    // Track previous pathname to detect actual navigation
    const [previousPath, setPreviousPath] = useState(location.pathname);

    // Auto-close iframe only when actual page navigation occurs
    useEffect(() => {
        // Only close iframe when pathname actually changes (real navigation)
        if (selectedLoanUrl && location.pathname !== previousPath) {
            console.log('🔄 Page navigation detected, auto-closing iframe.');
            console.log('Previous path:', previousPath);
            console.log('New path:', location.pathname);
            setSelectedLoanUrl('');
        }
        setPreviousPath(location.pathname);
    }, [location.pathname, selectedLoanUrl, setSelectedLoanUrl, previousPath]);

    const [bindMenuOutsideClickListener, unbindMenuOutsideClickListener] = useEventListener({
        type: 'click',
        listener: (event) => {
            const isOutsideClicked = !(
                sidebarRef.current?.isSameNode(event.target) || 
                sidebarRef.current?.contains(event.target) || 
                topbarRef.current?.menubutton?.isSameNode(event.target) || 
                topbarRef.current?.menubutton?.contains(event.target)
            );

            if (isOutsideClicked) {
                hideMenu();
            }
        }
    });

    const [bindDocumentResizeListener, unbindDocumentResizeListener] = useResizeListener({
        listener: () => {
            // Hide menu when switching from mobile to desktop
            if (isDesktop() && !isTouchDevice()) {
                hideMenu();
            }
            // Auto-close mobile menu when switching to desktop
            else if (isMobile() && layoutState.staticMenuMobileActive) {
                setLayoutState((prevState) => ({
                    ...prevState,
                    staticMenuMobileActive: false
                }));
            }
        }
    });

    const hideMenu = useCallback(() => {
        setLayoutState((prevLayoutState) => ({
            ...prevLayoutState,
            overlayMenuActive: false,
            overlaySubmenuActive: false,
            staticMenuMobileActive: false,
            menuHoverActive: false,
            menuClick: false,
            resetMenu: (isSlim() || isCompact() || isHorizontal()) && isDesktop()
        }));
    }, [isSlim, isCompact, isHorizontal, isDesktop, setLayoutState]);

    const blockBodyScroll = () => {
        if (document.body.classList) {
            document.body.classList.add('blocked-scroll');
        } else {
            document.body.className += ' blocked-scroll';
        }
    };

    const unblockBodyScroll = () => {
        if (document.body.classList) {
            document.body.classList.remove('blocked-scroll');
        } else {
            document.body.className = document.body.className.replace(new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
    };

    useEffect(() => {
        // Get user data from localStorage or your auth service
        const userData = localStorage.getItem('user');
        if (userData) {
            try {
                const parsedUser = JSON.parse(userData);
                console.log('User data loaded:', parsedUser);
                setUser(parsedUser);
            } catch (error) {
                console.error('Error parsing user data:', error);
                // Try to get user data from authToken
                const authToken = localStorage.getItem('authToken');
                if (authToken) {
                    // Decode JWT token if needed or set default user
                    setUser({
                        name: 'User',
                        email: 'user@example.com'
                    });
                }
            }
        }
    }, []);

    // Update iframe height based on window size
    useEffect(() => {
        const updateIframeHeight = () => {
            const width = window.innerWidth;
            if (width < 768) {
                setIframeHeight('500px');
            } else if (width < 1024) {
                setIframeHeight('600px');
            } else {
                setIframeHeight('700px');
            }
        };

        updateIframeHeight();
        window.addEventListener('resize', updateIframeHeight);
        return () => window.removeEventListener('resize', updateIframeHeight);
    }, []);

    useEffect(() => {
        if (layoutState.overlayMenuActive || layoutState.staticMenuMobileActive || layoutState.overlaySubmenuActive) {
            bindMenuOutsideClickListener();
        }

        if (layoutState.staticMenuMobileActive) {
            blockBodyScroll();
            (isSlim() || isCompact() || isHorizontal()) && bindDocumentResizeListener();
        }

        return () => {
            unbindMenuOutsideClickListener();
            unbindDocumentResizeListener();
            unblockBodyScroll();
        };
    }, [layoutState.overlayMenuActive, layoutState.staticMenuMobileActive, layoutState.overlaySubmenuActive]);

    useEffect(() => {
        hideMenu();
    }, [location]);

    const handleLogout = async () => {
        // Clear auth data
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        // Navigate to login
        navigate('/login');
    };

    const containerClassName = classNames(
        'layout-wrapper',
        {
            'layout-static': layoutConfig.menuMode === 'static',
            'layout-slim': layoutConfig.menuMode === 'slim',
            'layout-horizontal': layoutConfig.menuMode === 'horizontal',
            'layout-drawer': layoutConfig.menuMode === 'drawer',
            'layout-overlay': layoutConfig.menuMode === 'overlay',
            'layout-compact': layoutConfig.menuMode === 'compact',
            'layout-reveal': layoutConfig.menuMode === 'reveal',
            'layout-sidebar-dim': layoutConfig.colorScheme === 'dim',
            'layout-sidebar-dark': layoutConfig.colorScheme === 'dark',
            'layout-overlay-active': layoutState.overlayMenuActive,
            'layout-mobile-active': layoutState.staticMenuMobileActive,
            'layout-static-inactive': layoutState.staticMenuDesktopInactive && layoutConfig.menuMode === 'static',
            'p-input-filled': layoutConfig.inputStyle === 'filled',
            'p-ripple-disabled': !layoutConfig.ripple,
            'layout-sidebar-active': layoutState.sidebarActive,
            'layout-sidebar-anchored': layoutState.anchored
        },
        layoutConfig.colorScheme === 'light' ? 'layout-sidebar-' + layoutConfig.menuTheme : ''
    );

    return (
        <div className={containerClassName} data-theme={layoutConfig.colorScheme}>
            <a href="#main" className="skip-link sr-only focusable">Skip to main content</a>
            <ManualSwitchBackButton />
            <AppTopbar ref={topbarRef} user={user} onLogout={handleLogout} />
            <AppSidebar />
            <div className="layout-content-wrapper">
                <div className="layout-content" id="main" role="main">
                    {selectedLoanUrl ? (
                        <>
                            {console.log('🎯 Iframe should render now. URL:', selectedLoanUrl)}
                            <div className="grid">
                                <div className="col-12">
                                    <div className="card p-2 sm:p-3 md:p-4">
                                        <div className="flex justify-content-between align-items-center mb-2 md:mb-3">
                                            <h4 className="m-0 text-primary flex align-items-center">
                                                <i className="pi pi-calculator mr-2"></i>
                                                <span className="hidden sm:inline">Loan Calculator</span>
                                            </h4>
                                            <Button
                                                label="Back to Dashboard"
                                                icon="pi pi-arrow-left"
                                                className="p-button-secondary p-button-sm"
                                                onClick={() => {
                                                    console.log('🔄 Closing iframe, returning to previous page');
                                                    setSelectedLoanUrl('');
                                                }}
                                                aria-label="Back to Dashboard"
                                            />
                                        </div>
                                        <div className="border-round overflow-hidden iframe-container">
                                            <iframe
                                                src={selectedLoanUrl}
                                                title="Financial Calculator"
                                                className="w-full iframe-responsive"
                                                style={{ 
                                                    height: iframeHeight,
                                                    border: 'none',
                                                    backgroundColor: '#ffffff'
                                                }}
                                                frameBorder="0"
                                                allowFullScreen
                                                onLoad={() => console.log('✅ Iframe loaded successfully for URL:', selectedLoanUrl)}
                                                onError={() => console.error('❌ Iframe failed to load URL:', selectedLoanUrl)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            {console.log('📄 Rendering page content (no iframe)')}
                            <Outlet />
                        </>
                    )}
                </div>
                <AppFooter />
            </div>
            <AppSearch />
            <AppConfig />
            {/* <QuickCreateLeadFAB />  Temporarily disabled */}
            <div className="layout-mask"></div>
            <MobileBottomNav />
        </div>
    );
};

const DashboardLayout = () => {
    return (
        <LayoutProvider>
            <MaskingProvider>
                <MessagingProvider>
                    <NotificationProvider>
                        <DashboardLayoutWrapper />
                    </NotificationProvider>
                </MessagingProvider>
            </MaskingProvider>
        </LayoutProvider>
    );
};

export default DashboardLayout;